import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { fetchGames } from "../lib/api";
import { formatDate, formatLine, formatNumber } from "../lib/format";
import type { GameSummary } from "../types/product";

export function GamesPage() {
  const [games, setGames] = useState<GameSummary[]>([]);
  const [query, setQuery] = useState("");
  const [season, setSeason] = useState<number | "ALL">("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGames()
      .then(setGames)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const seasons = useMemo(
    () =>
      [...new Set(games.map((game) => game.season))].sort((a, b) => b - a),
    [games],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return games.filter((game) => {
      const text = [
        game.away_team,
        game.home_team,
        game.away_team_abbr,
        game.home_team_abbr,
      ]
        .join(" ")
        .toLowerCase();

      return (
        (!q || text.includes(q)) &&
        (season === "ALL" || game.season === season)
      );
    });
  }, [games, query, season]);

  return (
    <div className="page-stack">
      <section className="client-page-head">
        <div>
          <span>HISTORICAL ARCHIVE</span>
          <h1>Games</h1>
          <p>Browse completed halftime situations from 2023–2025.</p>
        </div>
        <strong>{filtered.length}</strong>
      </section>

      <section className="client-toolbar">
        <input
          aria-label="Search teams"
          placeholder="Search team..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        <div className="client-season-tabs">
          <button
            type="button"
            className={season === "ALL" ? "active" : ""}
            onClick={() => setSeason("ALL")}
          >
            All
          </button>

          {seasons.map((year) => (
            <button
              type="button"
              className={season === year ? "active" : ""}
              onClick={() => setSeason(year)}
              key={year}
            >
              {year}
            </button>
          ))}
        </div>
      </section>

      {loading ? <div className="empty-state">Loading games…</div> : null}
      {error ? <div className="empty-state error">{error}</div> : null}

      <section className="client-game-grid">
        {filtered.map((game) => (
          <Link
            className="client-game-card"
            to={`/games/${game.game_id}`}
            key={game.game_id}
          >
            <div className="client-game-date">
              {formatDate(game.game_date_utc)} · Week {game.week}
            </div>

            <div className="client-game-matchup">
              <div>
                <span>{game.away_team_abbr}</span>
                <strong>{game.away_score}</strong>
              </div>
              <em>HALF</em>
              <div>
                <span>{game.home_team_abbr}</span>
                <strong>{game.home_score}</strong>
              </div>
            </div>

            <div className="client-game-lines">
              <div>
                <span>Pregame spread</span>
                <strong>{formatLine(game.pregame_spread_home)}</strong>
              </div>
              <div>
                <span>Pregame total</span>
                <strong>{formatNumber(game.pregame_total)}</strong>
              </div>
            </div>

            <div className="client-game-open">Open game →</div>
          </Link>
        ))}
      </section>

      {!loading && !filtered.length ? (
        <div className="empty-state">No games match the current filters.</div>
      ) : null}
    </div>
  );
}
